import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, Layers, ShieldCheck, RefreshCw, Cpu, Store, Calendar } from 'lucide-react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const Mobili = () => {
  useDocumentMetadata({
    title: 'Mobili® Smart Medicindispenser | MedicCare',
    description: 'Mobili® er en ny, intelligent medicinhåndteringsløsning (edoset), der påminder borgeren samt dispenserer medicinen til rette tid og mængde med EOJ-integration.'
  });
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 py-20 lg:py-32 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili2_1.png" 
            alt="Mobili Hero" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-[#2c3223]/80 mix-blend-multiply"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Link 
              to="/medimi-smart" 
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-soft hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={14} /> Tilbage til Produkter
            </Link>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
              Mobili®
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed">
              Mobili® er en ny, intelligent medicinhåndteringsløsning (”edoset”), der påminder borgeren samt dispenserer medicinen til rette tid og i rette mængde.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="/#kontakt" 
                className="bg-white text-[#2c3223] text-xs font-bold uppercase tracking-widest px-8 py-4 rounded-md hover:bg-brand-soft transition-all"
              >
                Book en demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                Målgruppe &amp; Integration
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                Skabt til den mobile borger med fuld EOJ-integration
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Mobili® er særligt egnet til mobile borgere med et stabilt medicinskema, som har behov for 2-4 daglige doseringer. Den giver borgeren fuld frihed i hverdagen uden at gå på kompromis med sikkerheden.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Præcis som ”TIM pillerobotten” vil Mobili® hente og levere medicininformation direkte til og fra kommunens EOJ-system. Det sikrer en fuldstændig lukket og sikker datakæde, hvor plejen altid har det fulde overblik i realtid.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Egnet til mobile borgere</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Stabil medicinskema</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">2-4 daglige doseringer</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Fuld integration til EOJ</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-soft/50 rounded-3xl -rotate-2 z-0"></div>
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili2_1.png" 
                alt="Mobili Smart Dispenser" 
                className="rounded-2xl shadow-2xl relative z-10 w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Flexible Filling & Pharmacy Section */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Fleksibel påfyldning tilpasset jeres arbejdsgange</h2>
            <p className="text-slate-600">Mobili® tilbyder to forskellige måder at håndtere påfyldningen på, hvilket giver maksimal fleksibilitet for både kommune og apotek.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pakkemaskine Option */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
              <div>
                <div className="bg-brand-soft p-4 rounded-xl inline-flex text-[#2c3223] mb-6 w-16 h-16 items-center justify-center">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/lager.png" 
                    alt="Lager" 
                    className="w-10 h-10 object-contain" 
                  />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Brug som lokal ”pakkemaskine”</h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Mobili® kan fungere som en lokal pakkemaskine i kommunen. Systemet er understøttet af et intelligent IT-system, der guider personalet trin-for-trin i, hvordan hvert enkelt rum påfyldes korrekt. Dette minimerer risikoen for manuelle fejl og sikrer en hurtig og tryg arbejdsgang.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-brand">
                <CheckCircle2 size={16} /> Intelligent IT-styret påfyldningsguide
              </div>
            </div>

            {/* Apotekspakning Option */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
              <div>
                <div className="bg-brand-soft p-4 rounded-xl inline-flex text-[#2c3223] mb-6 w-16 h-16 items-center justify-center">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medidcin.png" 
                    alt="Apotek" 
                    className="w-10 h-10 object-contain" 
                  />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Apotekspakning &amp; Levering</h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  For at aflaste det kommunale personale mest muligt, kan I vælge at få pakket borgerens medicin direkte på et apotek. Apotekets eget personale kan efterfølgende levere og placere medicinen direkte ind i maskinen. Det sparer værdifuld tid i hjemmeplejen.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-brand">
                <CheckCircle2 size={16} /> Levering direkte i maskinen af apoteket
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capacity & Cassette Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 bg-slate-50 p-8 lg:p-12 rounded-3xl border border-slate-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Layers className="text-brand" /> Kassettekapacitet &amp; Ruter
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Mobili® indeholder **28 rum pr. kassette**, hvilket giver stor fleksibilitet i planlægningen af ruter og doseringer:
              </p>
              
              <div className="space-y-6">
                <div className="bg-white p-5 rounded-xl border border-slate-200/60 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center font-bold text-sm">1</span>
                    <h4 className="font-bold text-slate-900">14 dages rute (2 daglige doseringer)</h4>
                  </div>
                  <p className="text-sm text-slate-600 pl-11">
                    Her benyttes **1 kassette**, som dækker borgerens behov i 14 dage med 2 daglige doseringer (28 rum i alt).
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200/60 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center font-bold text-sm">2</span>
                    <h4 className="font-bold text-slate-900">14 dages rute (4 daglige doseringer)</h4>
                  </div>
                  <p className="text-sm text-slate-600 pl-11">
                    Her benyttes **2 kassetter**, som dækker borgerens behov i 14 dage med op til 4 daglige doseringer.
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                Kapacitet &amp; Fleksibilitet
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                28 rum pr. kassette tilpasset borgerens behov
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Kassettesystemet i Mobili® er designet til at gøre logistikken så enkel som muligt. Uanset om borgeren har brug for få eller mange daglige doseringer, kan systemet tilpasses, så genopfyldningsintervallerne holdes på et optimalt niveau (typisk hver 14. dag).
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Dette reducerer antallet af besøg i hjemmet udelukkende til medicinhåndtering, hvilket frigiver ressourcer i hjemmeplejen og giver borgeren mere uforstyrret tid i eget hjem.
              </p>
              <div className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <ShieldCheck size={24} className="text-brand shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-slate-900">Høj doseringssikkerhed</h4>
                  <p className="text-sm text-slate-600">Kassetten låses sikkert i maskinen, og kun den korrekte dosis frigives på det præcise tidspunkt.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2c3223] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ønsker du at høre mere om Mobili®?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Vi står klar til at rådgive om den rette løsning til jeres behov. Kontakt os i dag for en uforpligtende snak eller en demonstration af Mobili®.
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href="/#kontakt" 
              className="bg-white text-[#2c3223] font-bold text-xs uppercase tracking-widest px-8 py-4 rounded-full hover:bg-brand-soft transition-colors"
            >
              Kontakt os nu
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mobili;
